/* eslint-disable no-console */
import React, { FC, Suspense, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ActionButton,
  // CustomTable,
  EllipsisTooltipText,
  SearchBar,
  MaterialReactTable
} from '@components';
import { ProcessTableDataMap } from '@components/custom-table/types';
import { Avatar, Button } from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import { EmptySearchState, PurplePlusIcon } from '@assets/icons';
import { translate } from '@utils/locale';
import { Order } from '@components/table/types';
import { API_RESPONSE_STATUS } from '@constants/common';
import { ToastTypes } from '@type/toast';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';

import useGetTeamMembers, {
  TeamMembersTableHeader
} from '../hooks/useGetTeamMembers';
import { TeamMemberTableLimit } from '../constants';
import { useDeleteTeamMemberMutation } from '../api';
import AddButton from '../components/add-button/AddButton';
import AddTeamMembersModal from './AddTeamMembersModal';

const TeamMembersTable: FC<{ disableEdit?: boolean }> = ({
  disableEdit = false
}) => {
  const [addTeamMembersModalOpen, setAddTeamMembersModalOpen] =
    useState<boolean>(false);
  const [memberIdToEdit, setMemberIdToEdit] = useState('');

  const [deleteTeamMember] = useDeleteTeamMemberMutation();

  const {
    limit,
    offset,
    setLimit,
    setOffset,
    setSearchTerm,
    setSortBy,
    setSortOrder,
    sortBy,
    searchTerm,
    sortOrder,
    teamMembersData,
    totalRows,
    maxOrder,
    isLoading
  } = useGetTeamMembers();

  const { companyId } = useParams();

  const TEAM_MEMBER_LIST_ACTIONS = [
    {
      id: 'edit',
      label: translate('company.teamMembersTable.edit')
    },
    {
      id: 'delete',
      label: translate('company.teamMembersTable.delete')
    }
  ];

  const headerCells = [
    {
      id: 'no',
      label: 'No.',
      disableSort: true,
      showColumn: true,
      initialWidth: 40
    },
    {
      id: 'name',
      label: translate('company.teamMembersTable.name'),
      disableSort: true
    },
    {
      id: 'email',
      label: translate('company.teamMembersTable.email'),
      disableSort: true
    },
    {
      id: 'designation',
      label: translate('company.teamMembersTable.designation'),
      disableSort: true
    },
    {
      id: 'linkedinUrl',
      label: translate('company.teamMembersTable.linkedInURL'),
      disableSort: true
    },
    {
      id: 'description',
      label: translate('company.teamMembersTable.description'),
      disableSort: true
    },

    ...(disableEdit
      ? []
      : [
          {
            id: 'actions',
            label: translate('company.teamMembersTable.actions'),
            disableSort: true
          }
        ])
  ];

  const onDeleteTeamMember = async (teamMemberId: string) => {
    try {
      const res = await deleteTeamMember({
        companyId: companyId ?? '',
        teamMemberId: teamMemberId
      }).unwrap();
      if (res.status !== API_RESPONSE_STATUS.OK) {
        displayToast(
          ToastTypes.ERROR,
          res.error?.details?.[0] ?? 'Failed to delete team member'
        );
      }
      if (res.result === 'SUCCESS') {
        displayToast(ToastTypes.SUCCESS, 'Successfully deleted team member');
      } else if (res.result !== 'SUCCESS') {
        displayToast(
          ToastTypes.ERROR,
          res.error?.details?.[0] ?? 'Failed to delete team member'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const handleMenuItemClick = (
    optionId: string,
    teamMemberId: string,
    closeMenu: () => void
  ) => {
    if (optionId === 'edit') {
      setMemberIdToEdit(teamMemberId);
      setAddTeamMembersModalOpen(true);
    }
    if (optionId === 'delete') {
      onDeleteTeamMember(teamMemberId);
    }
    closeMenu();
  };

  const processTableData = useCallback(
    (
      data: Record<string, string | number | boolean>,
      index: number,
      closeMenu: () => void
    ): ProcessTableDataMap => {
      return {
        no: <div className="text-center">{index + 1}</div>,
        name: (
          <div className="flex gap-2 items-center">
            <div>
              {data.profileImagePath ? (
                <Avatar
                  sx={{
                    bgcolor: deepOrange[500],
                    width: 24,
                    height: 24
                  }}
                  variant="square"
                  src={(data.profileImagePath as string) ?? ''}
                />
              ) : (
                <Avatar variant="square" sx={{ width: 24, height: 24 }}>
                  {String(data.name).charAt(0).toUpperCase()}
                </Avatar>
              )}
            </div>
            <EllipsisTooltipText text={String(data?.name || '---')} />
          </div>
        ),

        email: (
          <div>
            <EllipsisTooltipText text={String(data?.email || '---')} />
          </div>
        ),

        designation: <div>{data.designation || '---'}</div>,

        secondaryEmail: (
          <div>
            <EllipsisTooltipText text={String(data?.secondaryEmail || '---')} />
          </div>
        ),

        linkedinUrl: (
          <EllipsisTooltipText text={String(data?.linkedinUrl || '---')} />
        ),
        description: (
          <EllipsisTooltipText text={String(data?.description || '---')} />
        ),
        actions: (
          <ActionButton
            handleMenuItemClick={option => {
              handleMenuItemClick(option, data.id as string, closeMenu);
            }}
            label={translate('company.teamMembersTable.actions')}
            options={TEAM_MEMBER_LIST_ACTIONS}
            renderEllipsis={true}
            isDisabled={disableEdit}
            hideDivider
            returnMenuItemsOnly
          />
        )
      };
    },
    []
  );

  const tableEmptyState = (
    <div className="flex flex-col gap-2 justify-center w-full h-[200px]">
      {disableEdit ? (
        <div className="flex justify-center">
          <EmptySearchState />
        </div>
      ) : (
        <>
          <div className="flex justify-center">
            <EmptySearchState />
          </div>
          {searchTerm && (
            <div className="text-center">
              {translate('company.investorsTable.emptyState', {
                searchTerm: searchTerm
              })}
            </div>
          )}
          <div className="flex justify-center">
            <AddButton
              customIcon={<></>}
              customVariantStyle="contained"
              handleButtonClick={() => setAddTeamMembersModalOpen(true)}
              label={translate('company.teamMembersTable.addTeamMember')}
              isDisabled={disableEdit}
            />
          </div>
        </>
      )}
    </div>
  );

  const handleOffsetChange = (newOffset: number) => {
    setOffset(newOffset);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
  };

  const handleChangeOrder = (newOrder: Order) => {
    setSortOrder(newOrder);
  };

  const handleSort = (value: TeamMembersTableHeader) => {
    setSortBy(value);
  };

  const handleRowClick = () => {};

  const onClickShowMore = () => {
    handleOffsetChange(offset + TeamMemberTableLimit);
  };

  const handleCloseModal = () => {
    setAddTeamMembersModalOpen(false);
    setMemberIdToEdit('');
  };

  return (
    <div>
      <div className="mt-14 text-xl font-semibold leading-8">
        {translate('company.teamMembersTable.teamMembers')}
      </div>
      <div className="flex justify-between pb-4 mt-4 w-[75%]">
        <div className="flex flex-row gap-4 w-[50%]">
          <SearchBar
            onSearch={setSearchTerm}
            placeholder={translate('dealListingPage.search')}
          />
        </div>
        <div className="hidden">
          <AddButton
            customStyle={{
              color: 'purple',
              borderColor: 'purple'
            }}
            customIcon={<PurplePlusIcon />}
            handleButtonClick={() => {}}
            label={translate('company.teamMembersTable.bulkUpload')}
          />
        </div>
      </div>
      <div
        className={`${
          teamMembersData.length && !disableEdit ? 'flex' : 'invisible'
        } gap-4 text-lg flex-row`}>
        <AddButton
          handleButtonClick={() => setAddTeamMembersModalOpen(true)}
          label={translate('company.teamMembersTable.addTeamMember')}
        />
      </div>

      <div className="mt-4">
        <Suspense fallback={<></>}>
          <MaterialReactTable
            processTableData={processTableData}
            tableData={teamMembersData ?? []}
            tableHeaders={headerCells}
            isLoading={isLoading}
            page={Math.floor(offset / limit) ?? 0}
            rowsPerPage={limit ?? 0}
            handleLimitChange={handleLimitChange}
            handleOffsetChange={handleOffsetChange}
            order={sortOrder}
            handleChangeOrder={handleChangeOrder}
            handleOrderBy={handleSort}
            orderBy={sortBy}
            totalRows={totalRows ?? 0}
            isRowClickable={true}
            handleRowClick={handleRowClick}
            elevation={0}
            disablePagination
            disableScrollToTop
            renderEmptyRowsFallback={() => tableEmptyState}
            disableColumnActionMenu
          />
        </Suspense>
        {totalRows > teamMembersData.length ? (
          <div className="flex justify-center w-full">
            <Button
              style={{ textTransform: 'none' }}
              variant="text"
              onClick={onClickShowMore}
              disabled={teamMembersData.length === totalRows}>
              {translate('showMore')}
            </Button>
          </div>
        ) : (
          <></>
        )}
      </div>
      {addTeamMembersModalOpen && (
        <AddTeamMembersModal
          isOpen={addTeamMembersModalOpen}
          handleCloseModal={handleCloseModal}
          memberDetails={
            memberIdToEdit
              ? teamMembersData.find(item => item.id === memberIdToEdit)
              : undefined
          }
          maxOrder={maxOrder}
          resetOffset={() => handleOffsetChange(0)}
        />
      )}
    </div>
  );
};

export default TeamMembersTable;
